/*
 * Minimal QR encoder — byte mode, error correction level M, versions 1–9
 * (up to 178 bytes of payload). Straight ISO/IEC 18004: Reed-Solomon EC
 * over GF(256), all 8 masks scored by penalty, format/version BCH bits.
 */

const EC_PER_BLOCK = [10, 16, 26, 18, 24, 16, 18, 22, 22];
const BLOCKS: [count: number, size: number][][] = [
  [[1, 16]],
  [[1, 28]],
  [[1, 44]],
  [[2, 32]],
  [[2, 43]],
  [[4, 27]],
  [[4, 31]],
  [[2, 38], [2, 39]],
  [[3, 36], [2, 37]],
];
const ALIGN = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
];

/* GF(256) with primitive polynomial 0x11d */
const EXP = new Uint8Array(510);
const LOG = new Uint8Array(256);
for (let i = 0, x = 1; i < 510; i++) {
  EXP[i] = x;
  if (i < 255) LOG[x] = i;
  x <<= 1;
  if (x & 0x100) x ^= 0x11d;
}
const gmul = (a: number, b: number) => (a && b ? EXP[LOG[a]! + LOG[b]!]! : 0);

function ecCodewords(data: number[], count: number): number[] {
  let gen = [1];
  for (let i = 0; i < count; i++) {
    const next = new Array<number>(gen.length + 1).fill(0);
    for (let j = 0; j < gen.length; j++) {
      next[j] = next[j]! ^ gen[j]!;
      next[j + 1] = next[j + 1]! ^ gmul(gen[j]!, EXP[i]!);
    }
    gen = next;
  }
  const rem = [...data, ...new Array<number>(count).fill(0)];
  for (let i = 0; i < data.length; i++) {
    const f = rem[i]!;
    if (f) for (let j = 1; j < gen.length; j++) rem[i + j]! ^= gmul(gen[j]!, f);
  }
  return rem.slice(data.length);
}

function buildCodewords(bytes: Uint8Array, version: number): number[] {
  const blocks = BLOCKS[version - 1]!;
  const ec = EC_PER_BLOCK[version - 1]!;
  const capacity = blocks.reduce((s, [n, d]) => s + n * d, 0);

  const bits: number[] = [];
  const push = (v: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((v >> i) & 1);
  };
  push(0b0100, 4); // byte mode
  push(bytes.length, 8);
  for (const b of bytes) push(b, 8);
  push(0, Math.min(4, capacity * 8 - bits.length)); // terminator
  while (bits.length % 8) bits.push(0);

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j]!;
    data.push(b);
  }
  for (let pad = 0xec; data.length < capacity; pad ^= 0xfd) data.push(pad);

  const dataBlocks: number[][] = [];
  let off = 0;
  for (const [n, sz] of blocks)
    for (let i = 0; i < n; i++) {
      dataBlocks.push(data.slice(off, off + sz));
      off += sz;
    }
  const ecBlocks = dataBlocks.map((b) => ecCodewords(b, ec));

  const out: number[] = [];
  const maxLen = Math.max(...dataBlocks.map((b) => b.length));
  for (let i = 0; i < maxLen; i++)
    for (const b of dataBlocks) if (i < b.length) out.push(b[i]!);
  for (let i = 0; i < ec; i++) for (const b of ecBlocks) out.push(b[i]!);
  return out;
}

/* 15-bit format info: 2 bits EC level (M = 00) + 3 bits mask + BCH(15,5) */
export function formatBits(mask: number): number {
  let rem = mask << 10;
  for (let i = 14; i >= 10; i--) if ((rem >> i) & 1) rem ^= 0x537 << (i - 10);
  return ((mask << 10) | rem) ^ 0x5412;
}

/* 18-bit version info: 6 bits version + BCH(18,6), only for version >= 7 */
export function versionBits(version: number): number {
  let rem = version << 12;
  for (let i = 17; i >= 12; i--) if ((rem >> i) & 1) rem ^= 0x1f25 << (i - 12);
  return (version << 12) | rem;
}

const MASKS: ((r: number, c: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => ((r >> 1) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function drawFormat(g: Uint8Array, size: number, mask: number) {
  const f = formatBits(mask);
  const bit = (i: number) => (f >> (14 - i)) & 1;
  const around: [number, number][] = [
    [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [8, 7], [8, 8], [7, 8],
    [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8],
  ];
  around.forEach(([r, c], i) => { g[r * size + c] = bit(i); });
  for (let i = 0; i < 7; i++) g[(size - 1 - i) * size + 8] = bit(i);
  for (let i = 7; i < 15; i++) g[8 * size + size - 15 + i] = bit(i);
}

function drawVersion(g: Uint8Array, size: number, version: number) {
  const v = versionBits(version);
  for (let i = 0; i < 18; i++) {
    const b = (v >> i) & 1;
    const r = Math.floor(i / 3);
    const c = size - 11 + (i % 3);
    g[r * size + c] = b;
    g[c * size + r] = b;
  }
}

function penalty(g: Uint8Array, size: number): number {
  const at = (r: number, c: number) => g[r * size + c]!;
  let score = 0;
  // N1: runs of 5+ same-colored modules in a row/column
  for (let r = 0; r < size; r++) {
    let runH = at(r, 0), lenH = 1;
    let runV = at(0, r), lenV = 1;
    for (let i = 1; i < size; i++) {
      if (at(r, i) === runH) lenH++;
      else { if (lenH >= 5) score += lenH - 2; runH = at(r, i); lenH = 1; }
      if (at(i, r) === runV) lenV++;
      else { if (lenV >= 5) score += lenV - 2; runV = at(i, r); lenV = 1; }
    }
    if (lenH >= 5) score += lenH - 2;
    if (lenV >= 5) score += lenV - 2;
  }
  // N2: 2x2 blocks of same color
  for (let r = 0; r < size - 1; r++)
    for (let c = 0; c < size - 1; c++) {
      const v = at(r, c);
      if (v === at(r, c + 1) && v === at(r + 1, c) && v === at(r + 1, c + 1)) score += 3;
    }
  // N3: finder-like patterns 1011101 with 0000 on one side
  const P1 = 0b10111010000;
  const P2 = 0b00001011101;
  for (let r = 0; r < size; r++) {
    let rowBits = 0, colBits = 0;
    for (let i = 0; i < size; i++) {
      rowBits = ((rowBits << 1) | at(r, i)) & 0x7ff;
      colBits = ((colBits << 1) | at(i, r)) & 0x7ff;
      if (i >= 10) {
        if (rowBits === P1 || rowBits === P2) score += 40;
        if (colBits === P1 || colBits === P2) score += 40;
      }
    }
  }
  // N4: dark/light balance
  let dark = 0;
  for (const v of g) dark += v;
  score += Math.floor(Math.abs((dark * 100) / (size * size) - 50) / 5) * 10;
  return score;
}

export function qrMatrix(text: string): { size: number; modules: Uint8Array } {
  const bytes = new TextEncoder().encode(text);
  let version = 0;
  for (let v = 1; v <= 9; v++) {
    const cap = BLOCKS[v - 1]!.reduce((s, [n, d]) => s + n * d, 0);
    if (12 + 8 * bytes.length <= cap * 8) { version = v; break; }
  }
  if (!version) throw new Error("qr: payload too long (max 178 bytes)");
  const size = version * 4 + 17;
  const grid = new Uint8Array(size * size);
  const func = new Uint8Array(size * size);
  const set = (r: number, c: number, v: number) => {
    grid[r * size + c] = v;
    func[r * size + c] = 1;
  };

  // finder patterns + separators
  for (const [r0, c0] of [[0, 0], [0, size - 7], [size - 7, 0]] as const) {
    for (let r = -1; r < 8; r++)
      for (let c = -1; c < 8; c++) {
        const rr = r0 + r, cc = c0 + c;
        if (rr < 0 || cc < 0 || rr >= size || cc >= size) continue;
        const on = r >= 0 && r < 7 && c >= 0 && c < 7 &&
          (r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4));
        set(rr, cc, on ? 1 : 0);
      }
  }
  // alignment patterns (skip the three that would sit on finders)
  for (const r of ALIGN[version - 1]!)
    for (const c of ALIGN[version - 1]!) {
      if (func[r * size + c]) continue;
      for (let dr = -2; dr <= 2; dr++)
        for (let dc = -2; dc <= 2; dc++)
          set(r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1 ? 1 : 0);
    }
  // timing patterns
  for (let i = 8; i < size - 8; i++) {
    if (!func[6 * size + i]) set(6, i, (i & 1) ^ 1);
    if (!func[i * size + 6]) set(i, 6, (i & 1) ^ 1);
  }
  // dark module + reserved format/version areas (values written per-mask below)
  set(size - 8, 8, 1);
  const reserve = (r: number, c: number) => { func[r * size + c] = 1; };
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) { reserve(8, i); reserve(i, 8); }
    if (i < 8) { reserve(8, size - 1 - i); reserve(size - 1 - i, 8); }
  }
  if (version >= 7)
    for (let i = 0; i < 18; i++) {
      const r = Math.floor(i / 3);
      const c = size - 11 + (i % 3);
      reserve(r, c);
      reserve(c, r);
    }

  // zigzag data placement
  const cw = buildCodewords(bytes, version);
  const totalBits = cw.length * 8;
  let bi = 0;
  let up = true;
  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col = 5; // skip the vertical timing column
    for (let i = 0; i < size; i++) {
      const r = up ? size - 1 - i : i;
      for (const c of [col, col - 1]) {
        if (func[r * size + c]) continue;
        grid[r * size + c] = bi < totalBits ? (cw[bi >> 3]! >> (7 - (bi & 7))) & 1 : 0;
        bi++;
      }
    }
    up = !up;
  }

  // try all 8 masks, keep the lowest penalty
  let best: Uint8Array | null = null;
  let bestScore = Infinity;
  for (let m = 0; m < 8; m++) {
    const g = grid.slice();
    const maskFn = MASKS[m]!;
    for (let r = 0; r < size; r++)
      for (let c = 0; c < size; c++)
        if (!func[r * size + c] && maskFn(r, c)) g[r * size + c]! ^= 1;
    drawFormat(g, size, m);
    if (version >= 7) drawVersion(g, size, version);
    const score = penalty(g, size);
    if (score < bestScore) { bestScore = score; best = g; }
  }
  return { size, modules: best! };
}

export function qrToCanvas(
  canvas: HTMLCanvasElement,
  text: string,
  opts: { width?: number; margin?: number; dark?: string; light?: string } = {},
) {
  const { size, modules } = qrMatrix(text);
  const margin = opts.margin ?? 4;
  const total = size + margin * 2;
  const width = opts.width ?? total * 4;
  // draw 1px per module, then scale up without smoothing for crisp edges
  const off = document.createElement("canvas");
  off.width = off.height = total;
  const octx = off.getContext("2d")!;
  octx.fillStyle = opts.light ?? "#ffffff";
  octx.fillRect(0, 0, total, total);
  octx.fillStyle = opts.dark ?? "#000000";
  for (let r = 0; r < size; r++)
    for (let c = 0; c < size; c++)
      if (modules[r * size + c]) octx.fillRect(c + margin, r + margin, 1, 1);
  canvas.width = canvas.height = width;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(off, 0, 0, total, total, 0, 0, width, width);
}
